import React, { useState, useEffect } from 'react';
import { branchAPI, workAssignmentAPI, holidayAPI, leaveAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import DirectAssignForm from '../components/DirectAssignForm';
import { FiBriefcase, FiShield, FiCalendar } from '../components/Icons';
import styled from 'styled-components';

// Page Container
const PageContainer = styled.div`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  font-family: ${props => props.theme?.fonts?.main || "'Sarabun', sans-serif"};
  box-sizing: border-box;
  max-width: none;
  margin: 0;
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xs};
  }
`;

const CalendarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
  gap: ${props => props.theme.spacing.lg};
  flex-wrap: wrap;
  background: ${props => props.theme.colors.surface};
  padding: ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  border: 1px solid ${props => props.theme.colors.border.light};
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: ${props => props.theme.spacing.md};
    margin-bottom: ${props => props.theme.spacing.lg};
    padding: ${props => props.theme.spacing.md};
    
    h2 {
      margin: 0;
      font-size: 1.5rem;
      text-align: center;
    }
  }
`;

const HeaderContent = styled.div`
  width: 100%;
  
  .header-top {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: ${props => props.theme.spacing.lg};
    
    @media (max-width: 768px) {
      flex-direction: column;
      gap: ${props => props.theme.spacing.md};
    }
  }
`;

const Button = styled.button`
  background: ${props => props.theme.colors.primary};
  color: ${props => props.theme.colors.text.inverse};
  border: none;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.medium};
  cursor: pointer;
  font-size: 14px;
  font-weight: 400;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: ${props => props.theme.shadows.light};
  
  &:hover {
    background: ${props => props.theme.colors.accent};
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
  
  @media (max-width: 768px) {
    padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
    font-size: 12px;
  }
`;

// NEW: Grid-based layout - Date cards containing branch cards
const DatesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(450px, 1fr));
  gap: ${props => props.theme.spacing.lg};
  width: 100%;
  
  @media (max-width: 1600px) {
    grid-template-columns: repeat(auto-fill, minmax(400px, 1fr));
  }
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const DateCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border-radius: ${props => props.theme.borderRadius.large};
  box-shadow: ${props => props.theme.shadows.card};
  border: 2px solid ${props => props.theme.colors.border.light};
  overflow: hidden;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  &.today {
    border-color: ${props => props.theme.colors.primary};
    box-shadow: 0 4px 20px rgba(0, 123, 255, 0.2);
  }
  
  &:hover {
    transform: translateY(-2px);
    box-shadow: ${props => props.theme.shadows.large};
  }
`;

const DateHeader = styled.div`
  padding: ${props => props.theme.spacing.md};
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  color: white;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  .date-info {
    display: flex;
    align-items: center;
    gap: ${props => props.theme.spacing.sm};
  }
  
  .day-number {
    font-size: 2rem;
    font-weight: 700;
    line-height: 1;
  }
  
  .date-details {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  
  .day-name {
    font-size: 0.9rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 1px;
  }
  
  .month-year {
    font-size: 0.75rem;
    opacity: 0.9;
  }
`;

const BranchesContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md};
`;

const BranchCard = styled.div`
  background: ${props => props.theme.colors.surface};
  border: 2px solid ${props => props.theme.colors.border.light};
  border-radius: ${props => props.theme.borderRadius.medium};
  padding: ${props => props.theme.spacing.sm};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  
  ${props => {
    if (props.staffingStatus === 'insufficient') {
      return `
        background: #fdf3f1;
        border-color: #e7c9c3;
      `;
    } else if (props.staffingStatus === 'adequate') {
      return `
        background: #eef5f0;
        border-color: #cfe3d7;
      `;
    }
    return '';
  }}
  
  ${props => props.isDirectAssignSelected && `
    background: #fbeae7 !important;
    border-color: #930c00 !important;
    border-width: 3px;
    box-shadow: 0 4px 12px rgba(33, 150, 243, 0.3);
  `}
  
  &:hover {
    transform: translateX(4px);
    box-shadow: ${props => props.theme.shadows.medium};
  }
`;

const BranchHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.sm};
  padding-bottom: ${props => props.theme.spacing.xs};
  border-bottom: 2px solid ${props => props.theme.colors.border.light};
  
  .branch-name {
    font-size: 1rem;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
  }
`;

const RoleSummary = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  
  .shift-summary {
    display: flex;
    gap: 3px;
    flex-wrap: wrap;
    align-items: center;
    
    .shift-label {
      font-size: 9px;
      font-weight: 700;
      color: ${props => props.theme.colors.text.secondary};
      min-width: 15px;
      text-transform: uppercase;
    }
    
    .role-count {
      display: inline-flex;
      align-items: center;
      background: ${props => props.theme.colors.surface};
      border: 1px solid ${props => props.theme.colors.border.light};
      border-radius: 10px;
      padding: 2px 5px;
      font-size: 9px;
      font-weight: 600;
      color: white;
      min-width: 16px;
      height: 16px;
      justify-content: center;
      
      &.manager { background: #e91e63; }
      &.makeup { background: #9c27b0; }
      &.photographer { background: #2196f3; }
      &.retoucher { background: #4caf50; }
      
      &.zero {
        background: #f5f5f5;
        color: #999;
        border-color: #e7e3db;
      }
    }
  }
`;

const Holiday = styled.div`
  background-color: ${props => {
    switch (props.group) {
      case 'A': return '#ffebee';
      case 'B': return '#e8f5e8';
      default: return '#fff3e0';
    }
  }};
  color: ${props => {
    switch (props.group) {
      case 'A': return '#c62828';
      case 'B': return '#2e7d32';
      default: return '#ef6c00';
    }
  }};
  padding: 4px 8px;
  margin: 4px 0;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  border-left: 3px solid currentColor;
  text-align: center;
`;

const ShiftSection = styled.div`
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const ShiftLabel = styled.div`
  font-size: 0.7rem;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  background: linear-gradient(135deg, ${props => props.theme.colors.secondary}, ${props => props.theme.colors.secondary}80);
  border-radius: ${props => props.theme.borderRadius.small};
  border-left: 3px solid ${props => props.theme.colors.primary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const Assignment = styled.div`
  background: ${props => {
    switch (props.shift) {
      case 'day': return 'linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)';
      case 'noon': return 'linear-gradient(135deg, #fff3e0 0%, #ffcc80 100%)';
      default: return 'linear-gradient(135deg, #f5f5f5 0%, #e0e0e0 100%)';
    }
  }};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.xs};
  margin: ${props => props.theme.spacing.xs} 0;
  border-radius: ${props => props.theme.borderRadius.medium};
  font-size: 0.75rem;
  font-weight: 500;
  border-left: 4px solid ${props => {
    switch (props.shift) {
      case 'day': return '#1976d2';
      case 'noon': return '#f57c00';
      default: return '#757575';
    }
  }};
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
  
  .employee-name {
    font-weight: 600;
    color: ${props => {
      switch (props.shift) {
        case 'day': return '#1565c0';
        case 'noon': return '#e65100';
        default: return '#424242';
      }
    }};
    font-size: 0.8rem;
    line-height: 1.2;
    margin-bottom: 2px;
  }
  
  .employee-role {
    font-size: 0.65rem;
    color: ${props => {
      switch (props.shift) {
        case 'day': return '#1976d2';
        case 'noon': return '#f57c00';
        default: return '#757575';
      }
    }};
    display: flex;
    align-items: center;
    gap: 4px;
  }
  
  .role-badge {
    display: inline-block;
    background: ${props => {
      switch (props.role) {
        case 'store_manager': return '#e91e63';
        case 'manager_assistant': return '#e91e63';
        case 'makeup': return '#9c27b0';
        case 'makeup_supervisor': return '#9c27b0';
        case 'photographer': return '#2196f3';
        case 'photographer_supervisor': return '#2196f3';
        case 'retoucher': return '#4caf50';
        case 'retoucher_supervisor': return '#4caf50';
        default: return '#757575';
      }
    }};
    color: white;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 0.6rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.3px;
    box-shadow: 0 1px 2px rgba(0,0,0,0.3);
  }
`;

const LeaveItem = styled.div`
  background-color: ${props => {
    switch (props.leaveType) {
      case 'Business leave': return '#e3f2fd';
      case 'Sick leave': return '#fff3e0';
      case 'Vacation leave': return '#e8f5e8';
      case 'Month leave': return '#f3e5f5';
      case 'Extra day leave': return '#fce4ec';
      default: return '#f5f5f5';
    }
  }};
  color: ${props => {
    switch (props.leaveType) {
      case 'Business leave': return '#1976d2';
      case 'Sick leave': return '#f57c00';
      case 'Vacation leave': return '#388e3c';
      case 'Month leave': return '#7b1fa2';
      case 'Extra day leave': return '#c2185b';
      default: return '#616161';
    }
  }};
  padding: 4px 6px;
  margin: 2px 0;
  border-radius: 4px;
  font-size: 11px;
  font-weight: bold;
  border-left: 3px solid currentColor;
`;

const MultiBranchManagementPage = () => {
  const [currentDate, setCurrentDate] = useState(() => {
    const now = new Date();
    return new Date(now.getFullYear(), now.getMonth(), 1);
  });
  
  const [branches, setBranches] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [leaveRequests, setLeaveRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [viewMode, setViewMode] = useState('assignments');
  const [filterRole, setFilterRole] = useState('');
  const [filterName, setFilterName] = useState('');

  // Map simplified role categories to actual roles (base + supervisor)
  const getRolesForCategory = (category) => {
    const roleMap = {
      'manager': ['store_manager', 'manager_assistant'],
      'makeup': ['makeup', 'makeup_supervisor'],
      'photographer': ['photographer', 'photographer_supervisor'],
      'retoucher': ['retoucher', 'retoucher_supervisor']
    };
    return roleMap[category] || [category];
  };
  
  // Direct assignment states
  const [isDirectAssignMode, setIsDirectAssignMode] = useState(false);
  const [directAssignSelectedDates, setDirectAssignSelectedDates] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  
  const { accessibleBranches, hasPermission } = useAuth();

  const getLocalDateString = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  useEffect(() => {
    fetchBranches();
  }, [accessibleBranches]);

  useEffect(() => {
    if (branches.length > 0) {
      fetchMonthData();
    }
  }, [currentDate, branches, filterName]);

  const fetchBranches = async () => {
    try {
      const response = await branchAPI.getAll();
      let accessibleBranchData = response.data;
      if (accessibleBranches !== 'ALL_BRANCHES') {
        accessibleBranchData = response.data.filter(branch => 
          accessibleBranches.includes(branch.id)
        );
      }
      setBranches(accessibleBranchData);
    } catch (error) {
      console.error('Error fetching branches:', error);
    }
  };

  const fetchMonthData = async () => {
    setLoading(true);
    try {
      const year = currentDate.getFullYear();
      const startDate = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
      const endDate = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);

      const [assignmentsRes, holidaysRes, leavesRes] = await Promise.all([
        workAssignmentAPI.getAll({
          start_date: getLocalDateString(startDate),
          end_date: getLocalDateString(endDate)
        }),
        holidayAPI.getAll({ year: year }),
        // /leave/requests has no year/month params — scope by start_date/end_date.
        leaveAPI.getLeaveRequests({
          start_date: getLocalDateString(startDate),
          end_date: getLocalDateString(endDate),
          status: 'approved'
        })
      ]);

      setAssignments(assignmentsRes.data || []);
      setHolidays(holidaysRes.data || []);
      setLeaveRequests(leavesRes.data || []);
    } catch (error) {
      console.error('Error fetching month data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getCalendarDays = () => {
    const year = currentDate.getFullYear();
    const month = currentDate.getMonth();
    const lastDay = new Date(year, month + 1, 0);
    
    const days = [];
    for (let date = 1; date <= lastDay.getDate(); date++) {
      days.push(new Date(year, month, date));
    }
    return days;
  };

  const getAssignmentsForBranchAndDate = (branchId, date) => {
    const dateStr = getLocalDateString(date);
    let filteredAssignments = assignments.filter(a =>
      a.branch_id === branchId && a.assignment_date === dateStr
    );

    // Apply role filter
    if (filterRole) {
      const allowedRoles = getRolesForCategory(filterRole);
      filteredAssignments = filteredAssignments.filter(a => allowedRoles.includes(a.role));
    }
    
    // Apply name filter
    if (filterName) {
      const nameFilter = filterName.toLowerCase();
      filteredAssignments = filteredAssignments.filter(a => 
        a.user?.name?.toLowerCase().includes(nameFilter)
      );
    }
    
    return filteredAssignments;
  };

  const getHolidaysForDate = (date) => {
    const dateStr = getLocalDateString(date);
    return holidays.filter(h => h.date === dateStr);
  };

  const getLeavesForBranchAndDate = (branchId, date) => {
    const dateStr = getLocalDateString(date);
    return leaveRequests.filter(l => {
      const user = l.user;
      return user && user.branch_id === branchId && l.leave_date === dateStr;
    });
  };

  const getRoleCountsByShift = (assignments) => {
    const dayShiftCounts = { manager: 0, makeup: 0, photographer: 0, retoucher: 0 };
    const noonShiftCounts = { manager: 0, makeup: 0, photographer: 0, retoucher: 0 };
    
    // Map actual roles to display categories
    const roleToCategory = {
      'store_manager': 'manager',
      'manager_assistant': 'manager',
      'makeup': 'makeup',
      'makeup_supervisor': 'makeup',
      'photographer': 'photographer',
      'photographer_supervisor': 'photographer',
      'retoucher': 'retoucher',
      'retoucher_supervisor': 'retoucher'
    };
    
    assignments.forEach(assignment => {
      const role = assignment.role;
      const shift = assignment.shift_type;
      const category = roleToCategory[role];
      
      if (category) {
        if (shift === 'day') {
          dayShiftCounts[category]++;
        } else if (shift === 'noon') {
          noonShiftCounts[category]++;
        }
      }
    });
    
    return { dayShiftCounts, noonShiftCounts };
  };

  const checkStaffingRequirements = (assignments) => {
    const dayShiftCounts = { makeup: 0, photographer: 0, manager: 0, retoucher: 0 };
    const noonShiftCounts = { makeup: 0, photographer: 0, manager: 0, retoucher: 0 };
    
    // Map actual roles to display categories
    const roleToCategory = {
      'store_manager': 'manager',
      'manager_assistant': 'manager',
      'makeup': 'makeup',
      'makeup_supervisor': 'makeup',
      'photographer': 'photographer',
      'photographer_supervisor': 'photographer',
      'retoucher': 'retoucher',
      'retoucher_supervisor': 'retoucher'
    };
    
    assignments.forEach(assignment => {
      const actualRole = assignment.role;
      const category = roleToCategory[actualRole];
      const shift = assignment.shift_type;
      
      if (category) {
        if (shift === 'day') {
          dayShiftCounts[category]++;
        } else if (shift === 'noon') {
          noonShiftCounts[category]++;
        }
      }
    });
    
    const dayShiftAdequate = dayShiftCounts.makeup >= 1 && dayShiftCounts.photographer >= 1;
    const noonShiftAdequate = noonShiftCounts.makeup >= 1 && noonShiftCounts.photographer >= 1;
    
    if (dayShiftAdequate && noonShiftAdequate) {
      return 'adequate';
    } else {
      return 'insufficient';
    }
  };

  const navigateMonth = (direction) => {
    setCurrentDate(prevDate => {
      const newDate = new Date(prevDate);
      newDate.setMonth(newDate.getMonth() + direction);
      return newDate;
    });
  };
  
  const handleMonthChange = (e) => {
    const [year, month] = e.target.value.split('-');
    setCurrentDate(new Date(parseInt(year), parseInt(month) - 1, 1));
  };
  
  const toggleDirectAssignMode = () => {
    setIsDirectAssignMode(prev => !prev);
    setDirectAssignSelectedDates([]);
  };
  
  const handleDirectAssignSuccess = async () => {
    setDirectAssignSelectedDates([]);
    await fetchMonthData();
  };
  
  const handleBranchCellClick = (branchId, date) => {
    if (isDirectAssignMode) {
      const dateStr = getLocalDateString(date);
      setDirectAssignSelectedDates(prev => {
        const isSelected = prev.includes(dateStr);
        if (isSelected) {
          return prev.filter(d => d !== dateStr);
        } else {
          return [...prev, dateStr];
        }
      });
      setSelectedBranch(branchId);
    }
  };
  
  const isDateDirectAssignSelected = (date) => {
    const dateStr = getLocalDateString(date);
    return directAssignSelectedDates.includes(dateStr);
  };

  const calendarDays = getCalendarDays();
  const today = new Date();

  return (
    <PageContainer>
      <CalendarHeader>
        <HeaderContent>
          <div className="header-top">
            <h2>Multi-Branch Management</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
              <Button 
                onClick={() => setViewMode('assignments')}
                style={{ backgroundColor: viewMode === 'assignments' ? '#930c00' : '#efe7d2', color: viewMode === 'assignments' ? '#ffffff' : '#6b645c' }}
              >
                <FiBriefcase style={{ marginRight: '4px' }} />
                Work Assignments
              </Button>
              <Button 
                onClick={() => setViewMode('leave')}
                style={{ backgroundColor: viewMode === 'leave' ? '#930c00' : '#efe7d2', color: viewMode === 'leave' ? '#ffffff' : '#6b645c' }}
              >
                <FiShield style={{ marginRight: '4px' }} />
                Leave Management
              </Button>
              {viewMode === 'assignments' && hasPermission(['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
                <Button 
                  onClick={toggleDirectAssignMode}
                  style={{ backgroundColor: isDirectAssignMode ? '#930c00' : '#efe7d2', color: isDirectAssignMode ? '#ffffff' : '#6b645c' }}
                >
                  {isDirectAssignMode ? 'Exit Direct' : 'Direct Assign'}
                </Button>
              )}
              
              {/* Filters - Only show in assignments view */}
              {viewMode === 'assignments' && (
                <>
                  <select
                    value={filterRole}
                    onChange={(e) => setFilterRole(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      border: '1px solid #e7e3db',
                      borderRadius: '4px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">All Roles</option>
                    <option value="manager">Manager</option>
                    <option value="makeup">Makeup</option>
                    <option value="photographer">Photographer</option>
                    <option value="retoucher">Retoucher</option>
                  </select>
                  
                  <input
                    type="text"
                    placeholder="Filter by name..."
                    value={filterName}
                    onChange={(e) => setFilterName(e.target.value)}
                    style={{
                      padding: '8px 12px',
                      fontSize: '12px',
                      border: '1px solid #e7e3db',
                      borderRadius: '4px',
                      minWidth: '150px'
                    }}
                  />
                  
                  {(filterRole || filterName) && (
                    <Button
                      onClick={() => {
                        setFilterRole('');
                        setFilterName('');
                      }}
                      style={{ backgroundColor: '#efe7d2', color: '#6b645c', padding: '8px 12px', fontSize: '12px' }}
                    >
                      Clear
                    </Button>
                  )}
                </>
              )}
              
              <Button onClick={() => navigateMonth(-1)}>« Prev</Button>
              
              {/* Month Selector */}
              <input
                type="month"
                value={`${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`}
                onChange={handleMonthChange}
                style={{
                  padding: '8px 12px',
                  fontSize: '14px',
                  border: '1px solid #e7e3db',
                  borderRadius: '4px',
                  fontWeight: 'bold'
                }}
              />
              
              <Button onClick={() => navigateMonth(1)}>Next »</Button>
            </div>
          </div>
        </HeaderContent>
      </CalendarHeader>

      {loading && <div style={{ textAlign: 'center', padding: '20px' }}>Loading...</div>}

      {/* Direct Assignment Form */}
      {viewMode === 'assignments' && hasPermission(['admin', 'store_manager', 'manager_assistant', 'makeup_supervisor', 'photographer_supervisor', 'retoucher_supervisor']) && (
        <DirectAssignForm
          isActive={isDirectAssignMode}
          onToggle={toggleDirectAssignMode}
          selectedBranchId={selectedBranch}
          currentDate={currentDate}
          selectedDates={directAssignSelectedDates}
          onDateSelectionChange={setDirectAssignSelectedDates}
          onAssignmentSuccess={handleDirectAssignSuccess}
          existingAssignments={assignments}
          holidays={holidays}
          leaveRequests={leaveRequests}
        />
      )}

      {/* Date Cards Grid */}
      <DatesGrid>
        {calendarDays.map((date, index) => {
          const dateStr = getLocalDateString(date);
          const isToday = date.toDateString() === today.toDateString();
          const dayHolidays = getHolidaysForDate(date);
          
          return (
            <DateCard key={index} className={isToday ? 'today' : ''}>
              <DateHeader>
                <div className="date-info">
                  <div className="day-number">{date.getDate()}</div>
                  <div className="date-details">
                    <div className="day-name">
                      {date.toLocaleDateString('en-US', { weekday: 'long' })}
                    </div>
                    <div className="month-year">
                      {date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                </div>
                {dayHolidays.length > 0 && (
                  <div style={{ fontSize: '1.5rem' }}>🎉</div>
                )}
              </DateHeader>
              
              {/* Display holidays at top of card */}
              {dayHolidays.length > 0 && (
                <div style={{ padding: '8px 12px', background: '#f8f9fa' }}>
                  {dayHolidays.map(holiday => (
                    <Holiday
                      key={holiday.id}
                      group={holiday.holiday_group?.name || 'Unknown'}
                    >
                      {holiday.name}
                    </Holiday>
                  ))}
                </div>
              )}
              
              <BranchesContainer>
                {branches.map(branch => {
                  const branchAssignments = getAssignmentsForBranchAndDate(branch.id, date);
                  const branchLeaves = getLeavesForBranchAndDate(branch.id, date);
                  const staffingStatus = viewMode === 'assignments' ? checkStaffingRequirements(branchAssignments) : null;
                  const { dayShiftCounts, noonShiftCounts } = getRoleCountsByShift(branchAssignments);
                  
                  const dayShifts = branchAssignments.filter(a => a.shift_type === 'day');
                  const noonShifts = branchAssignments.filter(a => a.shift_type === 'noon');
                  
                  const allRoles = ['store_manager', 'manager_assistant', 'makeup', 'makeup_supervisor', 'photographer', 'photographer_supervisor', 'retoucher', 'retoucher_supervisor'];
                  const groupByRole = (assignments) => {
                    const roleGroups = {};
                    allRoles.forEach(role => { roleGroups[role] = []; });
                    assignments.forEach(assignment => {
                      const role = assignment.role || 'unknown';
                      if (roleGroups[role]) { roleGroups[role].push(assignment); }
                    });
                    return roleGroups;
                  };
                  
                  const dayRoles = groupByRole(dayShifts);
                  const noonRoles = groupByRole(noonShifts);
                  
                  const isSelected = isDirectAssignMode && isDateDirectAssignSelected(date) && selectedBranch === branch.id;
                  
                  return (
                    <BranchCard
                      key={branch.id}
                      staffingStatus={staffingStatus}
                      isDirectAssignSelected={isSelected}
                      onClick={() => handleBranchCellClick(branch.id, date)}
                      style={{ cursor: isDirectAssignMode ? 'pointer' : 'default' }}
                    >
                      <BranchHeader>
                        <div className="branch-name">📍 {branch.name}</div>
                        {viewMode === 'assignments' && (
                          <RoleSummary>
                            <div className="shift-summary">
                              <span className="shift-label">D:</span>
                              <div className={`role-count manager ${dayShiftCounts.manager === 0 ? 'zero' : ''}`}>M:{dayShiftCounts.manager}</div>
                              <div className={`role-count makeup ${dayShiftCounts.makeup === 0 ? 'zero' : ''}`}>MU:{dayShiftCounts.makeup}</div>
                              <div className={`role-count photographer ${dayShiftCounts.photographer === 0 ? 'zero' : ''}`}>P:{dayShiftCounts.photographer}</div>
                              <div className={`role-count retoucher ${dayShiftCounts.retoucher === 0 ? 'zero' : ''}`}>R:{dayShiftCounts.retoucher}</div>
                            </div>
                            <div className="shift-summary">
                              <span className="shift-label">N:</span>
                              <div className={`role-count manager ${noonShiftCounts.manager === 0 ? 'zero' : ''}`}>M:{noonShiftCounts.manager}</div>
                              <div className={`role-count makeup ${noonShiftCounts.makeup === 0 ? 'zero' : ''}`}>MU:{noonShiftCounts.makeup}</div>
                              <div className={`role-count photographer ${noonShiftCounts.photographer === 0 ? 'zero' : ''}`}>P:{noonShiftCounts.photographer}</div>
                              <div className={`role-count retoucher ${noonShiftCounts.retoucher === 0 ? 'zero' : ''}`}>R:{noonShiftCounts.retoucher}</div>
                            </div>
                          </RoleSummary>
                        )}
                      </BranchHeader>
                      
                      {viewMode === 'assignments' && (
                        <>
                          {(dayShifts.length > 0 || noonShifts.length > 0) ? (
                            <>
                              <ShiftSection>
                                <ShiftLabel>Day Shift</ShiftLabel>
                                {allRoles.map(role => (
                                  <div key={`day-${role}`}>
                                    {dayRoles[role].map(assignment => (
                                      <Assignment key={assignment.id} shift="day" role={role}>
                                        <div className="employee-name">{assignment.user?.name || 'Unknown'}</div>
                                        <div className="employee-role">
                                          <span className="role-badge">{role}</span>
                                        </div>
                                      </Assignment>
                                    ))}
                                  </div>
                                ))}
                              </ShiftSection>
                              
                              <ShiftSection>
                                <ShiftLabel>Noon Shift</ShiftLabel>
                                {allRoles.map(role => (
                                  <div key={`noon-${role}`}>
                                    {noonRoles[role].map(assignment => (
                                      <Assignment key={assignment.id} shift="noon" role={role}>
                                        <div className="employee-name">{assignment.user?.name || 'Unknown'}</div>
                                        <div className="employee-role">
                                          <span className="role-badge">{role}</span>
                                        </div>
                                      </Assignment>
                                    ))}
                                  </div>
                                ))}
                              </ShiftSection>
                            </>
                          ) : (
                            <div style={{ textAlign: 'center', color: '#999', fontSize: '0.75rem', padding: '8px' }}>
                              No assignments
                            </div>
                          )}
                        </>
                      )}
                      
                      {viewMode === 'leave' && (
                        <>
                          {branchLeaves.length > 0 ? (
                            branchLeaves.map(leave => (
                              <LeaveItem key={leave.id} leaveType={leave.leave_type?.name}>
                                <FiShield style={{ marginRight: '4px' }} />
                                {leave.user?.name || 'Unknown'} - {leave.leave_type?.name}
                              </LeaveItem>
                            ))
                          ) : (
                            <div style={{ textAlign: 'center', color: '#999', fontSize: '0.75rem', padding: '8px' }}>
                              No leaves
                            </div>
                          )}
                        </>
                      )}
                    </BranchCard>
                  );
                })}
              </BranchesContainer>
            </DateCard>
          );
        })}
      </DatesGrid>
    </PageContainer>
  );
};

export default MultiBranchManagementPage;
